// src/components/sports/SportsSidebar.jsx - Mobile league filter restored

import React, { useState, useEffect } from 'react';
import { FootballAPI } from '../../services/footballApi';
import { BasketballAPI } from '../../services/basketballApi';
import { FiChevronDown, FiChevronRight, FiX, FiFilter, FiCheck } from 'react-icons/fi';

const SportsSidebar = ({ 
  selectedSport, 
  onSportSelect, 
  selectedDate, 
  onLeagueSelect, 
  isMobile, 
  onClose,
  fixtures 
}) => {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [isLeaguesOpen, setIsLeaguesOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('sports');

  // Sports order - Football first
  const sports = [
    { id: 'football', name: 'Football', icon: '⚽', color: 'bg-green-600' },
    { id: 'basketball', name: 'Basketball', icon: '🏀', color: 'bg-orange-600' },
    { id: 'tennis', name: 'Tennis', icon: '🎾', color: 'bg-yellow-600' },
    { id: 'baseball', name: 'Baseball', icon: '⚾', color: 'bg-red-600' },
    { id: 'hockey', name: 'Ice Hockey', icon: '🏒', color: 'bg-blue-600' },
    { id: 'esports', name: 'Esports', icon: '🎮', color: 'bg-purple-600' },
    { id: 'cricket', name: 'Cricket', icon: '🏏', color: 'bg-emerald-600' },
    { id: 'rugby', name: 'Rugby', icon: '🏉', color: 'bg-amber-600' },
  ];

  // League priority order
  const getLeaguePriority = (leagueId, leagueName) => {
    const priorityMap = {
      1: 1, 2: 2, 39: 3, 140: 4, 135: 5, 78: 6, 61: 7,
    };
    
    if (priorityMap[leagueId]) return priorityMap[leagueId];
    
    const name = leagueName?.toLowerCase() || '';
    if (name.includes('fifa world cup') || name.includes('world cup')) return 1;
    if (name.includes('champions league')) return 2;
    if (name.includes('premier league')) return 3;
    if (name.includes('la liga')) return 4;
    if (name.includes('serie a')) return 5;
    if (name.includes('bundesliga')) return 6;
    if (name.includes('ligue 1')) return 7;
    
    return 999;
  };

  // Extract leagues from fixtures
  const extractLeaguesFromFixtures = (fixturesData) => {
    if (!fixturesData || !Array.isArray(fixturesData) || fixturesData.length === 0) {
      return [];
    }

    const leagueMap = {};
    
    fixturesData.forEach(group => {
      if (group.league_id && group.league_name) {
        const leagueId = group.league_id;
        if (!leagueMap[leagueId]) {
          leagueMap[leagueId] = {
            id: leagueId,
            name: group.league_name,
            logo: group.league_logo || null,
            country: group.country || null,
            fixtureCount: 0
          };
        }
        if (group.fixtures && Array.isArray(group.fixtures)) {
          leagueMap[leagueId].fixtureCount += group.fixtures.length;
        }
      }
    });

    const result = Object.values(leagueMap);
    
    result.sort((a, b) => {
      const priorityA = getLeaguePriority(a.id, a.name);
      const priorityB = getLeaguePriority(b.id, b.name);
      return priorityA - priorityB;
    });

    return result;
  };

  // Update leagues when fixtures change
  useEffect(() => {
    if (fixtures && fixtures.length > 0) {
      const extractedLeagues = extractLeaguesFromFixtures(fixtures);
      setLeagues(extractedLeagues);
      setLoading(false);
    } else {
      setLeagues([]);
      setLoading(false);
    }
  }, [fixtures]);

  // Fallback fetch
  useEffect(() => {
    if (!fixtures || fixtures.length === 0) {
      fetchLeaguesFallback();
    }
  }, [selectedSport, selectedDate]);

  const fetchLeaguesFallback = async () => {
    setLoading(true);
    try {
      let response;
      
      if (selectedSport === 'football') {
        if (selectedDate) {
          response = await FootballAPI.getFixtures(selectedDate);
        } else {
          response = await FootballAPI.getUpcoming(100);
        }
      } else if (selectedSport === 'basketball') {
        if (selectedDate) {
          response = await BasketballAPI.getGames(selectedDate);
        } else {
          response = await BasketballAPI.getUpcomingGames(100);
        }
      }
      
      if (response && response.data && response.data.length > 0) {
        const extractedLeagues = extractLeaguesFromFixtures(response.data);
        setLeagues(extractedLeagues);
      } else {
        setLeagues([]);
      }
    } catch (error) {
      console.error('Failed to fetch leagues:', error);
      setLeagues([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLeagueClick = (leagueId) => {
    const newSelected = selectedLeague === leagueId ? null : leagueId;
    setSelectedLeague(newSelected);
    if (onLeagueSelect) {
      onLeagueSelect(newSelected);
    }
    // On mobile, keep the bottom sheet open but close after selection
    if (isMobile && onClose) {
      setTimeout(() => onClose(), 500);
    }
  };

  const handleSportSelect = (sportId) => {
    onSportSelect(sportId);
    setSelectedLeague(null);
    if (onLeagueSelect) {
      onLeagueSelect(null);
    }
    // Switch to leagues tab when football or basketball is selected
    if (sportId === 'football' || sportId === 'basketball') {
      setActiveTab('leagues');
    }
    if (isMobile && onClose) {
      setTimeout(() => onClose(), 300);
    }
  };

  const toggleLeagues = () => {
    setIsLeaguesOpen(!isLeaguesOpen);
  };

  const handleImageError = (e) => {
    e.target.style.display = 'none';
  };

  // Mobile Bottom Sheet Component
  if (isMobile) {
    return (
      <>
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
          onClick={onClose}
        />
        
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900 rounded-t-2xl z-50 animate-slide-up max-h-[85vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-800">
            <div className="flex items-center gap-2">
              <FiFilter className="w-5 h-5 text-blue-500" />
              <h3 className="text-lg font-bold text-white">Filters</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-full transition-colors"
            >
              <FiX className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          
          {/* Tab Bar */}
          <div className="flex border-b border-gray-800">
            <button
              onClick={() => setActiveTab('sports')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'sports'
                  ? 'text-blue-500 border-b-2 border-blue-500'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Sports
            </button>
            {(selectedSport === 'football' || selectedSport === 'basketball') && (
              <button
                onClick={() => setActiveTab('leagues')}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'leagues'
                    ? 'text-blue-500 border-b-2 border-blue-500'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Leagues ({leagues.length})
              </button>
            )}
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            {activeTab === 'sports' ? (
              <div className="grid grid-cols-2 gap-2">
                {sports.map((sport) => (
                  <button
                    key={sport.id}
                    onClick={() => handleSportSelect(sport.id)}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      selectedSport === sport.id
                        ? `${sport.color} text-white shadow-lg`
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <span className="text-2xl">{sport.icon}</span>
                    <span className="font-medium">{sport.name}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {/* "All Leagues" option - like date filter's "All" */}
                <button
                  onClick={() => handleLeagueClick(null)}
                  className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                    selectedLeague === null
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">📋</span>
                    <span className="font-medium">All Leagues</span>
                  </div>
                  {selectedLeague === null && (
                    <FiCheck className="w-5 h-5" />
                  )}
                </button>

                {loading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    <p className="text-sm text-gray-500 mt-3">Loading leagues...</p>
                  </div>
                ) : leagues.length > 0 ? (
                  leagues.map(league => {
                    const priority = getLeaguePriority(league.id, league.name);
                    const isTopLeague = priority <= 7;
                    const isWorldCup = league.id === 1 || league.name?.toLowerCase().includes('world cup');
                    const isSelected = selectedLeague === league.id;
                    
                    return (
                      <button
                        key={league.id}
                        onClick={() => handleLeagueClick(league.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                          isSelected
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        {league.logo ? (
                          <img 
                            src={league.logo} 
                            alt={league.name} 
                            className="w-6 h-6 object-contain"
                            onError={handleImageError}
                          />
                        ) : (
                          <span className="text-xl">{isWorldCup ? '🏆' : '📋'}</span>
                        )}
                        <span className="flex-1 text-left font-medium truncate">
                          {league.name}
                        </span>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {league.fixtureCount || 0}
                        </span>
                        {isSelected && (
                          <FiCheck className="w-5 h-5 flex-shrink-0" />
                        )}
                        {(isTopLeague || isWorldCup) && !isSelected && (
                          <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${
                            isWorldCup 
                              ? 'bg-yellow-500/20 text-yellow-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {isWorldCup ? 'Featured' : 'Top'}
                          </span>
                        )}
                      </button>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No leagues available</p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Apply Button */}
          <div className="p-4 border-t border-gray-800">
            <button
              onClick={onClose}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
        
        <style jsx>{`
          @keyframes slide-up {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          .animate-slide-up {
            animation: slide-up 0.3s ease-out;
          }
        `}</style>
      </>
    );
  }

  // Desktop Version
  return (
    <div className="bg-gray-900 rounded-xl p-4">
      <h3 className="text-lg font-bold text-white mb-4">Sports Categories</h3>
      
      <div className="space-y-1">
        {sports.map((sport) => (
          <React.Fragment key={sport.id}>
            <button
              onClick={() => handleSportSelect(sport.id)}
              className={`w-full flex items-center p-3 rounded-lg transition-colors ${
                selectedSport === sport.id
                  ? 'bg-blue-600 text-white'
                  : 'hover:bg-gray-800 text-gray-300'
              }`}
            >
              <span className="text-xl mr-3">{sport.icon}</span>
              <span className="text-sm sm:text-base">{sport.name}</span>
            </button>
            
            {/* Leagues Section - Desktop */}
            {(sport.id === 'football' || sport.id === 'basketball') && selectedSport === sport.id && (
              <div className="ml-4 mt-2 mb-2 pl-2 border-l-2 border-gray-700">
                <button
                  onClick={toggleLeagues}
                  className="w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-gray-800 transition-colors mb-1"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Leagues
                    </span>
                    <span className="text-xs text-gray-500">
                      ({leagues.length})
                    </span>
                  </div>
                  {isLeaguesOpen ? (
                    <FiChevronDown className="w-4 h-4 text-gray-400" />
                  ) : (
                    <FiChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </button>
                
                {isLeaguesOpen && (
                  <div className="space-y-1 mt-1">
                    {loading ? (
                      <div className="text-center py-4">
                        <div className="inline-block animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"></div>
                        <p className="text-xs text-gray-500 mt-2">Loading leagues...</p>
                      </div>
                    ) : leagues.length > 0 ? (
                      <div className="space-y-1 max-h-96 overflow-y-auto custom-scrollbar">
                        {leagues.map(league => {
                          const priority = getLeaguePriority(league.id, league.name);
                          const isTopLeague = priority <= 7;
                          const isWorldCup = league.id === 1 || league.name?.toLowerCase().includes('world cup');
                          
                          return (
                            <button
                              key={league.id}
                              onClick={() => handleLeagueClick(league.id)}
                              className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded transition-colors ${
                                selectedLeague === league.id
                                  ? 'bg-blue-600/20 text-blue-400'
                                  : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
                              }`}
                            >
                              {league.logo ? (
                                <img 
                                  src={league.logo} 
                                  alt={league.name} 
                                  className="w-4 h-4 object-contain"
                                  onError={handleImageError}
                                />
                              ) : (
                                <span className="w-4 h-4 text-xs">
                                  {isWorldCup ? '🏆' : '📋'}
                                </span>
                              )}
                              <span className="truncate flex-1 text-left">
                                {league.name}
                              </span>
                              <span className="text-[10px] text-gray-500 flex-shrink-0">
                                {league.fixtureCount || 0}
                              </span>
                              {isTopLeague && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 flex-shrink-0">
                                  Top
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-xs text-gray-500">No leagues available</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #1f2937;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #4b5563;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #6b7280;
        }
      `}</style>
    </div>
  );
};

export default SportsSidebar;